/**
 * The invented fleet.
 *
 * Every name here is fictional, and deliberately looks it. The product's own
 * screenshots cannot be used on a public page — the real ones carry live project
 * names and operator email addresses — so every "screenshot" on this site is a
 * hand-built replica fed from this file.
 *
 * The hygiene rules, so nothing is ever mistaken for a real deployment:
 *   · hosts are `srv-ist-1`, `srv-fra-1`, `nuc-lab` — places, not customers
 *   · projects are `atlas-web`, `mercury-api`, `kepler-veri`
 *   · operators are `@ornek` / `@example.com`, which RFC 2606 reserves
 *   · every id begins `01JQ8`, every digest `sha256:0f0f`
 * A single grep over this directory proves no real data leaked.
 *
 * The *codes* are not invented. Rejection reasons are the real constants from
 * `internal/domain/routing.go`, and their Turkish is copied from the console's
 * own catalogue (`web/src/i18n/tr.ts`), so the page speaks the product's exact
 * vocabulary rather than a marketing paraphrase of it.
 */

export interface Candidate {
  id: string
  account: string
  model: string
  host: string
  /** Null when the candidate survived eligibility. */
  reject: RejectionCode | null
  /** Score, for the survivors. */
  score?: number
}

export type RejectionCode =
  | 'ACCOUNT_DISABLED'
  | 'ACCOUNT_AT_CONCURRENCY_LIMIT'
  | 'ACCOUNT_NOT_REACHABLE_FROM_HOST'
  | 'MODEL_UNAVAILABLE_FOR_ACCOUNT'
  | 'HOST_OFFLINE'
  | 'PROVIDER_CLI_NOT_INSTALLED'
  | 'REPOSITORY_NOT_AVAILABLE_ON_HOST'

/** The console's own Turkish for each code — copied, not re-translated. */
export const REJECTION_TR: Record<RejectionCode, string> = {
  ACCOUNT_DISABLED: 'hesap kapalı',
  ACCOUNT_AT_CONCURRENCY_LIMIT: 'eşzamanlılık sınırında',
  ACCOUNT_NOT_REACHABLE_FROM_HOST: 'bu host’tan erişilemez',
  MODEL_UNAVAILABLE_FOR_ACCOUNT: 'model bu hesapta yok',
  HOST_OFFLINE: 'host çevrimdışı',
  PROVIDER_CLI_NOT_INSTALLED: 'CLI kurulu değil',
  REPOSITORY_NOT_AVAILABLE_ON_HOST: 'repo host’ta yok',
}

export const REJECTION_EN: Record<RejectionCode, string> = {
  ACCOUNT_DISABLED: 'account disabled',
  ACCOUNT_AT_CONCURRENCY_LIMIT: 'at its concurrency limit',
  ACCOUNT_NOT_REACHABLE_FROM_HOST: 'not reachable from this host',
  MODEL_UNAVAILABLE_FOR_ACCOUNT: 'model not on this account',
  HOST_OFFLINE: 'host offline',
  PROVIDER_CLI_NOT_INSTALLED: 'CLI not installed',
  REPOSITORY_NOT_AVAILABLE_ON_HOST: 'repository not on this host',
}

/** The pools, named as the console names them. */
export const POOL_KEYS = ['account', 'model', 'host', 'project', 'role'] as const
export type PoolKey = (typeof POOL_KEYS)[number]

/**
 * Twelve candidates reach the gate and five pass it.
 *
 * The proportion is the point, and it is the same one the sign-in screen's plate
 * makes: routing is mostly refusal, and the refusals are what the decision record
 * exists to explain.
 */
export const CANDIDATES: readonly Candidate[] = [
  { id: '01JQ8A1', account: 'claude-ops-1', model: 'opus-5', host: 'srv-ist-1', reject: null, score: 0.78 },
  { id: '01JQ8A2', account: 'claude-ops-2', model: 'opus-5', host: 'srv-ist-1', reject: 'ACCOUNT_AT_CONCURRENCY_LIMIT' },
  { id: '01JQ8A3', account: 'claude-ops-2', model: 'sonnet-5', host: 'srv-fra-1', reject: null, score: 0.64 },
  { id: '01JQ8A4', account: 'codex-ops-1', model: 'gpt-5-codex', host: 'srv-ist-1', reject: null, score: 0.61 },
  { id: '01JQ8A5', account: 'codex-research', model: 'gpt-5-codex', host: 'nuc-lab', reject: 'PROVIDER_CLI_NOT_INSTALLED' },
  { id: '01JQ8A6', account: 'llm-yerel', model: 'qwen-30b', host: 'nuc-lab', reject: 'MODEL_UNAVAILABLE_FOR_ACCOUNT' },
  { id: '01JQ8A7', account: 'claude-ops-1', model: 'opus-5', host: 'srv-fra-1', reject: 'REPOSITORY_NOT_AVAILABLE_ON_HOST' },
  { id: '01JQ8A8', account: 'claude-arsiv', model: 'sonnet-5', host: 'srv-ist-1', reject: 'ACCOUNT_DISABLED' },
  { id: '01JQ8A9', account: 'codex-ops-1', model: 'gpt-5-codex', host: 'nuc-lab', reject: 'HOST_OFFLINE' },
  { id: '01JQ8AA', account: 'llm-yerel', model: 'qwen-30b', host: 'srv-fra-1', reject: 'ACCOUNT_NOT_REACHABLE_FROM_HOST' },
  { id: '01JQ8AB', account: 'claude-ops-2', model: 'sonnet-5', host: 'srv-ist-1', reject: null, score: 0.55 },
  { id: '01JQ8AC', account: 'codex-research', model: 'gpt-5-codex', host: 'srv-fra-1', reject: null, score: 0.41 },
] as const

/** The decision the hero and the ledger both describe. */
export const DECISION = {
  id: '01JQ8F3K2M',
  componentTotal: 0.86,
  penaltyTotal: 0.08,
  total: 0.78,
  winner: 'claude-ops-1',
} as const

/** The fleet, for the fleet board. A `null` gauge is unmeasured — and it must
 *  stay null rather than becoming a zero, which is the section's whole claim. */
export interface Host {
  name: string
  state: 'online' | 'degraded'
  cpu: number | null
  ram: number | null
  disk: number | null
  gpu: number | null
}

export const HOSTS: readonly Host[] = [
  { name: 'srv-ist-1', state: 'online', cpu: 0.34, ram: 0.52, disk: 0.19, gpu: 0.4 },
  { name: 'srv-ist-2', state: 'online', cpu: 0.12, ram: 0.31, disk: 0.44, gpu: 0.08 },
  { name: 'srv-fra-1', state: 'degraded', cpu: 0.71, ram: 0.66, disk: 0.23, gpu: 0.55 },
  // The one that carries the argument: a host reporting everything but its GPU.
  { name: 'nuc-lab', state: 'online', cpu: 0.22, ram: 0.4, disk: 0.31, gpu: null },
] as const
